"use client";

import React from "react";
import { Icon10, Icon10Forward, PauseIcon, PlayIcon, SoundIcon } from "@/components/icons";
import { RewindBackIcon, RewindForwardIcon } from "@/components/icons";
import type { PulseIconState, SkipPulseState } from "./hooks/usePlayerPulse";

interface PlayerPulseProps {
  pulseIcon: PulseIconState;
  skipPulse: SkipPulseState;
}

/**
 * Overlay badges of the player. Absolutely positioned, so the parent needs
 * `relative`; never intercepts pointer events.
 */
export function PlayerPulse({ pulseIcon, skipPulse }: PlayerPulseProps) {
  return (
    <>
      {pulseIcon && (
        <div
          key={pulseIcon.key}
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-40"
        >
          <div className="bg-black/50 rounded-full p-6 animate-video-pulse">
            {pulseIcon.type === "play" && (
              <PlayIcon size={48} className="w-12 h-12 text-white" />
            )}
            {pulseIcon.type === "pause" && (
              <PauseIcon size={48} className="w-12 h-12 text-white" />
            )}
            {pulseIcon.type === "unmute" && (
              <SoundIcon size={48} className="w-12 h-12 text-white" />
            )}
            {pulseIcon.type === "mute" && (
              <span className="relative block w-12 h-12 leading-none after:content-[''] after:absolute after:left-[-4px] after:right-[-4px] after:top-1/2 after:h-[4px] after:-translate-y-1/2 after:bg-white after:rotate-45 after:rounded-full">
                <SoundIcon size={48} className="w-12 h-12 text-white block" />
              </span>
            )}
          </div>
        </div>
      )}

      {skipPulse && (
        <div
          key={skipPulse.key}
          className={`absolute inset-y-0 ${
            skipPulse.direction === "backward" ? "left-0 w-1/3" : "right-0 w-1/3"
          } flex items-center justify-center pointer-events-none z-40`}
        >
          <div className="bg-black/50 rounded-full p-5 animate-video-pulse text-white flex items-center gap-2">
            {skipPulse.seconds === 10 ? (
              skipPulse.direction === "forward" ? (
                <Icon10Forward size={36} aria-hidden="true" />
              ) : (
                <Icon10 size={36} aria-hidden="true" />
              )
            ) : (
              <>
                {skipPulse.direction === "backward" ? (
                  <RewindBackIcon size={36} className="w-9 h-9 text-white" aria-hidden="true" />
                ) : (
                  <RewindForwardIcon size={36} className="w-9 h-9 text-white" aria-hidden="true" />
                )}
                <span className="text-lg font-semibold">{skipPulse.seconds}</span>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
